import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import {
  ApiErrorBody,
  getErrorResponseData,
  is2FAAlreadyEnabled,
} from '@app/routing/Session/ErrorResponse'

import { useNavigate } from '@lib/react-router-dom'

import { ChangeTradingAccountPasswordValues } from '@pages/Traders-Room/Trading-Accounts/ChangeTradingAccountPassword/ChangeTradingAccountPassword.page'
import { ChangeTradingAccountPasswordVerifyForm } from '@pages/Traders-Room/Trading-Accounts/ChangeTradingAccountPassword/ChangeTradingAccountPasswordVerifyForm'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { isJsonString } from '@utils/isJsonString'

import { TwoFAProviderEnum } from '@model/TwoFactorAuthentication'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'
import { InformationModal } from '@shared/modals/Information.modal'

import styles from './ChangeTradingAccountPasswordVerify.module.scss'

interface LocationParams {
  tradingAccountId: string
  [key: string]: string
}

interface ChangeTradingAccountPasswordVerifyProps {
  tradingAccountId: string
  token: string
  password: string
  changeInvestorPassword: boolean
  changeTickmillTraderPassword: boolean
  resetChangePasswordData(): void
  onPasswordRestSubmit(values?: ChangeTradingAccountPasswordValues): Promise<void>
  onPasswordRestSubmitSuccess?(): void
}

export const ChangeTradingAccountPasswordVerifyPage: React.FC<
  ChangeTradingAccountPasswordVerifyProps
> = (props) => {
  const { token, password, changeInvestorPassword, changeTickmillTraderPassword } = props
  const { onPasswordRestSubmit, onPasswordRestSubmitSuccess } = props

  const { tradingAccountId } = useParams<LocationParams>()

  const { t } = useTranslation()
  const locale = useSessionLanguage()
  const navigate = useNavigate()
  const apiClient = useApiClient(ClientApiClient)

  const { account } = useAccountReadContext()

  const [error, setError] = useState<ApiErrorBody | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const handleSubmit = async (code: string) => {
    if (!tradingAccountId || !password || !token) return

    setLoading(true)
    setError(null)

    try {
      await enable2FA(code)
      await changePassword(tradingAccountId)
      onPasswordRestSubmitSuccess
        ? onPasswordRestSubmitSuccess()
        : navigate(`/dashboard/traders-room/trading-accounts`)
      handleOpenModal()
    } catch (err) {
      const { response, errorMessage } = getErrorResponseData(err as ResponseError)

      const data = response?.response?.data

      setError({
        message: isJsonString(errorMessage) ? JSON.parse(errorMessage) : errorMessage,
        properties: data?.properties,
      })

      throw err
    } finally {
      setLoading(false)
    }
  }

  const enable2FA = async (code: string) => {
    try {
      await apiClient.get2FAActionConfirmationOtp(
        {
          typeId: TwoFAProviderEnum.Email,
          twoFactor: { code, token, typeId: TwoFAProviderEnum.Email },
        },
        locale
      )
    } catch (error) {
      if (!is2FAAlreadyEnabled(error)) {
        throw error
      }
    }
  }

  const changePassword = async (tradingAccountId: string) => {
    await apiClient.changeTradingAccountPassword({
      tradingAccountId,
      password,
      changeInvestorPassword,
      languageId: locale,
    })
  }

  const handleOpenModal = () => {
    if (changeInvestorPassword) {
      investorModal.open()
    } else if (changeTickmillTraderPassword) {
      tmtModal.open()
    } else {
      taModal.open()
    }
  }

  const taModal = useModal(InformationModal, {
    title: t('Trading Account.Trading Account Password Changed Successfully!'),
    description: t('Trading Account.Your new password is now active'),
  })

  const investorModal = useModal(InformationModal, {
    title: t('Trading Account.Investor Password Changed Successfully!'),
    description: t('Trading Account.Your new password is now active'),
  })

  const tmtModal = useModal(InformationModal, {
    title: t('Trading Account.Trading Account User ID Password Changed Successfully'),
    description: t(
      'Trading Account.Note, the same password will be used to access all your Trading Accounts created in the Tickmill Trader platform'
    ),
  })

  if (!tradingAccountId || !password || !token) {
    return null
  }

  return (
    <div className={styles.wrapper}>
      <Box
        flex
        fullWidth
        direction='column'
        justify='flex-start'
        align='center'
        gap='x24'
        alignSelf='stretch'
        textAlign='center'
      >
        <Loading isLoading={loading} showLoadingIcon>
          <ChangeTradingAccountPasswordVerifyForm
            t={t}
            tradingAccountId={tradingAccountId}
            account={account}
            validationError={error}
            onPasswordRestSubmit={onPasswordRestSubmit}
            onSubmit={handleSubmit}
          />
        </Loading>
      </Box>
    </div>
  )
}
